import { type UseMutateAsyncFunction } from "@tanstack/react-query";
import { message } from "antd";
import { type AxiosResponse } from "axios";
import { useContext, useState } from "react";
import { type RouteComponentProps } from "react-router";

import { BTPopover } from "@btnet/core-components/BTPopover";
import { useMfaEnrollmentStateGetV2 } from "@btnet/services-btnet/handlers/mfa-enrollment-state/mfa-enrollment-state";
import type { BadRequestResponse } from "@btnet/services-btnet/handlers/models/badRequestResponse";
import type { IdpUserUpdateRequest } from "@btnet/services-btnet/handlers/models/idpUserUpdateRequest";
import { useUsersByUserIdPatchV2 } from "@btnet/services-btnet/handlers/users/users";

import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";

import { type ErrorType } from "utilities/api/apix-handler";
import { Auth0MyAccountProvider } from "utilities/auth0MyAccount/Auth0MyAccountProvider";

import { optInEveryLoginAndLogout } from "commonComponents/auth/AuthStoreDiscovery.utils";
import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { btConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTForm, BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSwitch } from "commonComponents/btWrappers/BTSwitch/BTSwitch";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { Email } from "commonComponents/entity/email/InputEmail/InputEmail.types";
import { EditUsernameModal } from "commonComponents/entity/user/EditUsernameModal/EditUsernameModal";
import { ResetPasswordButton } from "commonComponents/entity/user/ResetPasswordButton/ResetPasswordButton";
import { type IBTInjectedFormikProps } from "commonComponents/utilities/Formik/WithFormikBT";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";

import { InternalUserLoginStatus } from "entity/internalUser/InternalUserDetails/common/InternalUserLoginStatus";
import { type IInternalUserHandler } from "entity/internalUser/InternalUserDetails/InternalUser.api.handler";
import {
    type IInternalUserFormValues,
    type InternalUserEntity,
} from "entity/internalUser/InternalUserDetails/InternalUser.api.types";
import { handleLogout } from "entity/login/Login/Login.utils";
import { Auth0MyAccountMfa } from "entity/mfa/Auth0MyAccountMfa/Auth0MyAccountMfa";

import "./InternalUsersSecurityLoginTab.less";

const MFA_SELF_ENROLL_COPY = {
    buttonLabel: "Set up two-factor authentication (2FA)",
    confirmTitle: "Enroll in two-factor authentication (2FA)",
    confirmContent:
        "Enrolling in two-factor authentication will log you out and send you to set up your second factor. If two-factor authentication is already configured, this will simply require you to login with it.",
    confirmOk: "Enroll & log out",
    // Shown next to the button only when we have no enrollment record and therefore can't confirm
    // the user's current 2FA state without an extra management call.
    unknownStatusTooltip:
        "If two-factor authentication is already configured, this will simply require you to login with it.",
} as const;

export interface ISecurityLoginTabProps extends RouteComponentProps {
    internalUserEntity: InternalUserEntity;
    isSelf: boolean;
    canEdit: boolean;
    handler?: IInternalUserHandler;
    onSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
    inEditMode: boolean;
}

type MutateAsyncType = UseMutateAsyncFunction<
    AxiosResponse<void, unknown>,
    ErrorType<BadRequestResponse>,
    {
        userId: number;
        data: IdpUserUpdateRequest;
    },
    unknown
>;

const ChangeUsernameModal: React.FC<{
    internalUserEntity: InternalUserEntity;
    isSelf: boolean;
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    showEditUsername: boolean;
    setShowEditUsername: React.Dispatch<React.SetStateAction<boolean>>;
    mutateAsync: MutateAsyncType;
}> = ({
    internalUserEntity,
    isSelf,
    loading,
    setLoading,
    showEditUsername,
    setShowEditUsername,
    mutateAsync,
}) => {
    const appDefaults = useContext(AppDefaultInfoContext);
    const editorModalCopy = isSelf
        ? "When you save, you'll be logged out of Buildertrend. Log in again using your new username."
        : "When you save, the user will have to log in using their updated username.";
    const newEmailHelperText = "Your new username must be a valid email address";
    const newEmailSubmitText = isSelf ? "Save & log out" : "Save";
    const shouldLogOut = isSelf;
    const successToast = "Emails sent";

    const handleEditUsernameSuccess = shouldLogOut
        ? () => {
              handleLogout(appDefaults);
          }
        : () => {
              setShowEditUsername(false);
              void message.success(successToast);
          };

    const handleUsernameEdited = async (newUsername: string) => {
        await mutateAsync({
            userId: internalUserEntity.internalUser.userId,
            data: { emailAddress: newUsername },
        });
    };

    return (
        <EditUsernameModal
            bodyCopy={editorModalCopy}
            currentUsername={internalUserEntity.internalUser.username}
            loading={loading}
            newEmailHelperText={newEmailHelperText}
            newEmailSubmitText={newEmailSubmitText}
            visible={showEditUsername}
            onBeforeClose={() => setShowEditUsername(false)}
            onEditUsernameSuccess={handleEditUsernameSuccess}
            onUsernameEdited={handleUsernameEdited}
            setLoading={setLoading}
        />
    );
};

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type ChangeUsernameSchema = {
    newUsername: string;
};

/**
 * Self-serve MFA enrollment control on the Security & Login tab.
 *
 * Shown only to the user viewing their own account. Reuses the shared
 * {@link optInEveryLoginAndLogout} CTA (the same path the cohort block screen and nudge banner use)
 * so enrollment stays a single, hardened flow: logout -> Auth0 re-login with every-login MFA.
 *
 * Visibility derives from the Auth0-owned axes (hasFactor/hasEveryLogin), which the backend carries
 * whether or not the user is in the cohort:
 * - confirms every-login + an enrolled factor: hide the button — they're already covered.
 * - otherwise show the button. When no enrollment doc exists (`!isApplicable`) we also show an
 *   "unknown status" disclaimer — we can't confirm their MFA state without an extra management call.
 */
const SelfMfaEnrollButton: React.FC<{ userId: number }> = ({ userId }) => {
    const { data: response } = useMfaEnrollmentStateGetV2();
    const enrollmentState = response?.data;

    // hasFactor/hasEveryLogin are the Auth0-owned axes; the backend now carries them even when the
    // user is outside the cohort (isApplicable false). Gate purely on them so a fully-enrolled user
    // hides the button regardless of cohort membership.
    const fullyEnrolled = Boolean(enrollmentState?.hasFactor && enrollmentState?.hasEveryLogin);
    if (fullyEnrolled) {
        return null;
    }

    const hasUnknownStatus = !enrollmentState?.isApplicable;

    const handleEnrollClick = () => {
        btConfirm({
            title: MFA_SELF_ENROLL_COPY.confirmTitle,
            content: MFA_SELF_ENROLL_COPY.confirmContent,
            okText: MFA_SELF_ENROLL_COPY.confirmOk,
            onOk: () => optInEveryLoginAndLogout(userId),
        });
    };

    return (
        <span className="flex items-center gap-2">
            {hasUnknownStatus && (
                <BTPopover
                    data-testid="mfaSelfEnroll-unknownStatus"
                    content={MFA_SELF_ENROLL_COPY.unknownStatusTooltip}
                />
            )}
            <BTButton type="secondary" data-testid="mfaSelfEnroll-cta" onClick={handleEnrollClick}>
                {MFA_SELF_ENROLL_COPY.buttonLabel}
            </BTButton>
        </span>
    );
};

export function InternalUsersSecurityLoginTab({
    internalUserEntity,
    isSelf,
    values,
    canEdit,
    setValues,
    onSubmit,
    setFieldValue,
    inEditMode,
}: IBTInjectedFormikProps<ISecurityLoginTabProps, IInternalUserFormValues>) {
    const builderInfo = useContext(BuilderInfoContext);
    const isNew = internalUserEntity.internalUser.id === 0;
    const hasUsername = internalUserEntity.internalUser.username !== "";
    const showMfaManagement = isSelf && !!builderInfo?.flags.selfServiceMfaManagement;

    const showLoginStatus = !isNew;
    const showArchiveButton = canEdit && !isSelf && !isNew;
    const [showEditUsername, setShowEditUsername] = useState(false);
    const [loading, setLoading] = useState(false);

    const { mutateAsync } = useUsersByUserIdPatchV2();

    const handleArchiveUser = () => {
        if (values.userEnabled) {
            btConfirm({
                title: "Archive User",
                content:
                    "Archiving this User will disable their login and remove them from all Internal User lists on new jobs. You will be able to recover and re-activate any Archived Users.",
                okText: "Archive User",
                onOk: () => {
                    const newValues = {
                        ...values,
                        userEnabled: false,
                        loginEnabled: false,
                    };
                    setValues(newValues);
                    onSubmit();
                },
            });
        } else {
            btConfirm({
                title: "Confirm",
                content:
                    "Unarchiving a user will allow them to reappear on future jobs. Activate their login on the Security & Login tab of Internal User Details.",
                okText: "Unarchive User",
                onOk: () => {
                    setFieldValue("userEnabled", true);
                    onSubmit();
                },
            });
        }
    };

    return (
        <PageSection
            title="Security & Login"
            className="SecurityAndLoginsTab"
            extra={
                <BTRow gutter={8}>
                    {isSelf && (
                        <BTCol>
                            <SelfMfaEnrollButton userId={internalUserEntity.internalUser.userId} />
                        </BTCol>
                    )}
                    {isSelf && (
                        <BTCol>
                            <ResetPasswordButton isSelf={isSelf} />
                        </BTCol>
                    )}
                    {internalUserEntity.canChangeEmail && (
                        <BTCol>
                            <BTButton
                                type="secondary"
                                data-testid="btnEditUsername"
                                onClick={() => setShowEditUsername(true)}
                                disabled={!inEditMode}
                            >
                                Edit username
                            </BTButton>
                        </BTCol>
                    )}
                </BTRow>
            }
        >
            <BTForm>
                <BTRow className="margin-bottom-sm">
                    <BTCol>
                        <BTRow gutter={8}>
                            <BTCol>
                                <BTTitle level={4}>Login Access</BTTitle>
                            </BTCol>
                            {!isSelf && showLoginStatus && (
                                <BTCol>
                                    <InternalUserLoginStatus
                                        internalUserStatusData={internalUserEntity.internalUser}
                                    />
                                </BTCol>
                            )}
                        </BTRow>
                        {isSelf && showLoginStatus && (
                            <BTRow>
                                <BTCol>
                                    <InternalUserLoginStatus
                                        internalUserStatusData={internalUserEntity.internalUser}
                                    />
                                </BTCol>
                            </BTRow>
                        )}
                        {!isSelf && (
                            <BTRow gutter={16}>
                                <BTCol xs={24}>
                                    <BTFormItemAutomatic<IInternalUserFormValues>
                                        id="loginEnabled"
                                        label=""
                                        className="LoginEnabled"
                                    >
                                        <BTSwitch
                                            id="loginEnabled"
                                            data-testid="loginEnabled"
                                            checked={values.loginEnabled}
                                            onCheckedChange={(f, value) => {
                                                if (value) {
                                                    if (
                                                        !values.primaryEmail ||
                                                        !values.primaryEmail.emailAddress
                                                    ) {
                                                        setValues({
                                                            ...values,
                                                            primaryEmail: new Email(),
                                                            loginEnabled: true,
                                                        });
                                                    } else {
                                                        setFieldValue("loginEnabled", true);
                                                    }
                                                } else {
                                                    setValues({
                                                        ...values,
                                                        loginEnabled: false,
                                                        sendInvitation: false,
                                                    });
                                                }
                                            }}
                                            label="Active"
                                            disabled={
                                                !internalUserEntity.canView ||
                                                !canEdit ||
                                                !values.userEnabled ||
                                                !inEditMode
                                            }
                                        />
                                    </BTFormItemAutomatic>
                                </BTCol>
                            </BTRow>
                        )}
                        {hasUsername && (
                            <>
                                <BTRow className="margin-top-md">
                                    <BTCol>
                                        <BTTitle level={4}>Username</BTTitle>
                                    </BTCol>
                                </BTRow>
                                <BTRow>
                                    <BTCol>
                                        <span>{internalUserEntity.internalUser.username}</span>
                                    </BTCol>
                                </BTRow>
                            </>
                        )}
                        <ChangeUsernameModal
                            internalUserEntity={internalUserEntity}
                            isSelf={isSelf}
                            loading={loading}
                            setLoading={setLoading}
                            showEditUsername={showEditUsername}
                            setShowEditUsername={setShowEditUsername}
                            mutateAsync={mutateAsync}
                        />
                        {showArchiveButton && (
                            <>
                                <BTRow className="margin-top-md">
                                    <BTCol>
                                        <BTTitle level={4}>Archive</BTTitle>
                                    </BTCol>
                                </BTRow>
                                <BTRow>
                                    <BTCol>
                                        <span>
                                            <BTButton
                                                data-testid="archive-user"
                                                onClick={handleArchiveUser}
                                                disabled={!canEdit || !inEditMode}
                                            >
                                                {values.userEnabled ? "Archive User" : "Unarchive"}
                                            </BTButton>
                                        </span>
                                    </BTCol>
                                </BTRow>
                            </>
                        )}
                    </BTCol>
                </BTRow>
                {showMfaManagement && (
                    <BTRow className="margin-top-md">
                        <BTCol xs={24}>
                            <Auth0MyAccountProvider>
                                <Auth0MyAccountMfa />
                            </Auth0MyAccountProvider>
                        </BTCol>
                    </BTRow>
                )}
            </BTForm>
        </PageSection>
    );
}
